// メインアプリケーションファイル
// _modulesディレクトリのクラスをimportして使用

import HamburgerMenu from './_modules/HamburgerMenu.js';
import ScrollAnimation from './_modules/ScrollAnimation.js';
import SearchForm from './_modules/SearchForm.js';
import ScreenInfo from './_modules/ScreenInfo.js';
import Accordion from './_modules/Accordion.js';
import SlideFade from './_modules/SlideFade.js';
import SplitText from './_modules/SplitText.js';
import Switcher from './_modules/Switcher.js';
import InnerLink from './_modules/InnerLink.js';
import CreateInnerLinks from './_modules/CreateInnerLinks.js';
import Refine from './_modules/Refine.js';
import SyntaxHighlight from './_modules/SyntaxHighlight.js';
import Notice from './_modules/Notice.js';
import CookieConsent from './_modules/CookieConsent.js';
import BgParticles from './_modules/BgParticles.js';
import LogoLoop from './_modules/LogoLoop.js';
import SlidePoints from './_modules/SlidePoints.js';
import SlideBanner from './_modules/SlideBanner.js';
import SlideKnowhow from './_modules/SlideKnowhow.js';
import SlidePickup from './_modules/SlidePickup.js';
import SlidePrecedent from './_modules/SlidePrecedent.js';
import Gallery from './_modules/Gallery.js';
import ListView from './_modules/ListView.js';
import SidebarFacets from './_modules/SidebarFacets.js';
import MindMap from './_modules/MindMap.js';
import ScrollTop from './_modules/ScrollTop.js';
import Diagnosis from './_modules/Diagnosis.js';
import HsForm from './_modules/HsForm.js';
import SeminarVideo from './_modules/SeminarVideo.js';
import YouTubePlayer from './_modules/YouTubePlayer.js';
import TableHint from './_modules/TableHint.js';
import MediaTextOrder from './_modules/MediaTextOrder.js';

// アプリケーションクラス
class App {
  constructor() {
    this.modules = [];
    this.init();
  }

  init() {
    // console.log('Platform App initialized');

    // DOM読み込み完了後に初期化
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupModules());
    } else {
      this.setupModules();
    }
  }

  setupModules() {
    // SyntaxHighlight（シンタックスハイライト）を最初に初期化
    // 他のモジュールが動作する前にjs-*クラスを無効化
    const syntaxHighlightElements = document.querySelectorAll('.js-highlight');
    if (syntaxHighlightElements.length > 0) {
      const syntaxHighlight = new SyntaxHighlight();
      this.modules.push(syntaxHighlight);
    }

    // 画面情報の管理を初期化
    const screenInfo = new ScreenInfo();
    this.modules.push(screenInfo);

    // ListView（一覧：年別JSON結合＋ページネーション。MT100件制限回避）の初期化
    // JSON取得はサーバー応答待ちが長くなることがあるため、スライダー等の重い初期化より先に発火させる
    const listElements = document.querySelectorAll('.js-list');
    listElements.forEach((el) => {
      this.modules.push(new ListView(el));
    });

    // ハンバーガーメニューの初期化
    const hamburgerTrigger = document.querySelector('.js-hamburger');
    if (hamburgerTrigger) {
      const hamburgerMenu = new HamburgerMenu();
      this.modules.push(hamburgerMenu);
    }

    // スクロールアニメーションの初期化
    const scrollElements = document.querySelectorAll('.js-scroll-anim');
    if (scrollElements.length > 0) {
      const scrollAnim = new ScrollAnimation();
      this.modules.push(scrollAnim);
      // グローバルに参照できるようにする（Switcherから参照）
      window.scrollAnimationInstance = scrollAnim;
    }

    // 検索フォームの初期化
    const searchTrigger = document.querySelector('.js-searchform-trigger');
    if (searchTrigger) {
      const searchForm = new SearchForm();
      this.modules.push(searchForm);
    }

    // アコーディオンの初期化
    // コードハイライトブロック内の要素は除外
    const accordionElements = document.querySelectorAll('.js-ac:not(.js-highlight .js-ac):not(pre .js-ac):not(code .js-ac)');
    if (accordionElements.length > 0) {
      const accordion = new Accordion();
      this.modules.push(accordion);
    }

    // SplitText（文字送りアニメ用：テキストを1文字ずつspan化）の初期化
    // スライダー初期化／スクロールリビールより前に分割しておく（.charが揃ってから表示制御に乗せる）。
    // KVコピー（.js-split-text）＋ 見出しの文字送りリビール（.js-reveal-text）の両方を対象。
    const splitTextElements = document.querySelectorAll('.js-split-text, .js-reveal-text');
    if (splitTextElements.length > 0) {
      this.modules.push(new SplitText({ selector: '.js-split-text, .js-reveal-text' }));
    }

    // 見出し等のスクロールイン リビール（SplitTextの後＝.charが揃ってから監視）。
    // 対象：c-heading（和文.jaをclip）／.js-reveal（要素をclip）／.js-reveal-text（文字送り）。
    // JS有効時のみ has-heading-reveal を付与（JS無効なら常時表示）。画面内に入ったら is-inview。
    const revealElements = document.querySelectorAll('.c-heading, .js-reveal, .js-reveal-text');
    if (revealElements.length > 0 && 'IntersectionObserver' in window) {
      document.documentElement.classList.add('has-heading-reveal');
      // 画面に入ってすぐではなく、下から約20%入ってから発火（演出を落ち着かせる）。
      const revealObserver = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            obs.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -20% 0px' });
      revealElements.forEach((el) => {
        // data-duration でパーツ単位に clip 系のアニメ時間を上書き（CSS変数 --reveal-duration）。文字送りは data-split-* で調整。
        const dur = el.dataset.duration;
        if (dur) {
          el.style.setProperty('--reveal-duration', /^\d+(\.\d+)?$/.test(dur) ? `${dur}s` : dur);
        }
        revealObserver.observe(el);
      });
    }

    // SlideFade（汎用フェードスライダー：KV画像/コピー・強みパネル等）の初期化
    // 要素ごとに1インスタンス生成（複数インスタンス対応）。pagination等はインスタンス単位でスコープ解決される。
    const slideFadeElements = document.querySelectorAll('.js-slide-fade');
    slideFadeElements.forEach((element) => {
      this.modules.push(new SlideFade({ element }));
    });

    // Switcherの初期化
    const switcherElements = document.querySelectorAll('.js-switcher');
    if (switcherElements.length > 0) {
      const switcher = new Switcher();
      this.modules.push(switcher);
    }

    // SeminarVideo（TOP無料集客セミナー：アクティブなステージをYouTubeインライン自動再生）の初期化
    if (document.querySelector('.js-seminar')) {
      this.modules.push(new SeminarVideo());
    }

    // 単体の動画（.p-movie＝記事内のアーカイブ動画等）の初期化。制御は SeminarVideo と共通の YouTubePlayer
    document.querySelectorAll('.js-movie').forEach((element) => {
      this.modules.push(new YouTubePlayer(element, {
        holderSelector: '.js-movie-player',
        iframeClass: 'p-movie-iframe',
      }));
    });

    // InnerLink（ページ内リンク）の初期化
    // Switcherの後に初期化することで、Switcherのハッシュを正しく除外
    const innerLink = new InnerLink();
    this.modules.push(innerLink);

    // CreateInnerLinks（ページ内リンク一覧生成）の初期化
    // コードハイライトブロック内の要素は除外
    const createInnerLinksElements = document.querySelectorAll('.js-create-innerlinks:not(.js-highlight .js-create-innerlinks):not(pre .js-create-innerlinks):not(code .js-create-innerlinks)');
    if (createInnerLinksElements.length > 0) {
      const createInnerLinks = new CreateInnerLinks();
      this.modules.push(createInnerLinks);
    }

    // Refine（絞り込み機能）の初期化
    // コードハイライトブロック内の要素は除外
    const refineElements = document.querySelectorAll('.js-refine:not(.js-highlight .js-refine):not(pre .js-refine):not(code .js-refine)');
    if (refineElements.length > 0) {
      const refine = new Refine();
      this.modules.push(refine);
    }

    // Notice（ヘッダー通知の閉じる制御）の初期化
    const noticeElements = document.querySelectorAll('.js-notice');
    if (noticeElements.length > 0) {
      const notice = new Notice();
      this.modules.push(notice);
    }

    // CookieConsent（Cookie同意バナー：同意でN日間非表示）の初期化
    const cookieElement = document.querySelector('.js-cookie');
    if (cookieElement) {
      const cookieConsent = new CookieConsent();
      this.modules.push(cookieConsent);
    }

    // BgParticles（背景パーティクルアニメーション：全画面最背面・マウス反応）の初期化
    const bgParticlesElement = document.querySelector('.js-bg-particles');
    if (bgParticlesElement) {
      const bgParticles = new BgParticles();
      this.modules.push(bgParticles);
    }

    // LogoLoop（ロゴ帯の横流れループ：トラック複製＋CSSアニメ）の初期化
    const logoLoopElements = document.querySelectorAll('.js-logo-loop');
    if (logoLoopElements.length > 0) {
      this.modules.push(new LogoLoop());
    }

    // SlidePoints（強みPoint群：SP=スワイプ / PC=2×2グリッド）の初期化
    const slidePointsElements = document.querySelectorAll('.js-slide-points');
    if (slidePointsElements.length > 0) {
      this.modules.push(new SlidePoints());
    }

    // SlideBanner（事例の実績バナー：中央ループ・画像のみ）の初期化
    const slideBannerElements = document.querySelectorAll('.js-slide-banner');
    if (slideBannerElements.length > 0) {
      this.modules.push(new SlideBanner());
    }

    // SlideKnowhow（最新ノウハウの記事カードスライダー：PC3枚 / SP中央1枚＋ちら見）の初期化
    const slideKnowhowElements = document.querySelectorAll('.js-slide-knowhow');
    if (slideKnowhowElements.length > 0) {
      this.modules.push(new SlideKnowhow());
    }

    // SlidePickup（ピックアップ共通：ループ。data属性で幅/自動再生/操作ボタンを切替。資料DLバナーも共用）の初期化
    const slidePickupElements = document.querySelectorAll('.js-slide-pickup');
    if (slidePickupElements.length > 0) {
      this.modules.push(new SlidePickup());
    }

    // SlidePrecedent（事例カード：SP=スワイプ / PC=3列グリッド）の初期化
    const slidePrecedentElements = document.querySelectorAll('.js-slide-precedent');
    if (slidePrecedentElements.length > 0) {
      this.modules.push(new SlidePrecedent());
    }

    // Gallery（採用ギャラリー。件数＋data属性しきい値でモード決定＝fixed/slider/flow）の初期化
    const galleryElements = document.querySelectorAll('.js-gallery');
    if (galleryElements.length > 0) {
      this.modules.push(new Gallery());
    }

    // MediaTextOrder（メディア＋テキスト：SPだけ画像を見出しの直後へ移す）の初期化
    const mediaTextElements = document.querySelectorAll('.p-wysiwyg .mt-be-columns.is-mediatext');
    if (mediaTextElements.length > 0) {
      this.modules.push(new MediaTextOrder());
    }

    // ScrollTop（ページトップへ戻るボタン：100pxスクロールで表示）の初期化
    if (document.querySelector('.js-totop')) {
      this.modules.push(new ScrollTop());
    }

    // SidebarFacets（記事詳細：サイドバー カテゴリ/タグ一覧をJSONから生成）の初期化
    // ListLoaderキャッシュで ListView と同一JSONを共有＝多重読込しない。一覧ページ内(.l-archive)は除外
    if (document.querySelector('.p-sidebar-cats[data-sidebar-cats], .p-sidebar-tags[data-sidebar-tags]')) {
      this.modules.push(new SidebarFacets());
    }

    // MindMap（課題解決マインドマップ：複数JSON取得＋タブ連動チップ＋悩みアコーディオン）の初期化
    if (document.querySelector('.js-mindmap')) {
      this.modules.push(new MindMap());
    }

    // Diagnosis（広告運用診断 Yes/Noチャート：SP対話式）の初期化
    if (document.querySelector('.js-diagnosis')) {
      this.modules.push(new Diagnosis());
    }

    // HsForm（HubSpot埋め込みフォーム：読み込み中スピナー→完了でフェードイン）の初期化
    if (document.querySelector('.js-hsform')) {
      this.modules.push(new HsForm());
    }

    // TableHint（SP横スクロール表のスワイプ誘導：初回スクロールで消す）の初期化
    if (document.querySelector('.js-table-hint')) {
      this.modules.push(new TableHint());
    }

    this.revealSliders();
  }

  // スライダー類はCSS（_module/js/_js-slider.scss）で初期化前を不可視にしている。
  // 全モジュールのマウント後にFOUC抑止クラスを付与し、レイアウト確定後に表示する。
  // querySelectorAllは初期化の成否に依存しないため、初期化失敗時も無表示のまま残らない。
  revealSliders() {
    document.querySelectorAll('.js-slide-fade, .js-slide-points, .js-slide-banner, .js-slide-knowhow, .js-slide-pickup, .js-slide-precedent, .js-logo-loop, .js-gallery')
      .forEach((el) => el.classList.add('is-slider-ready'));
  }
}

// アプリケーション開始
new App();

export default App;
